import { AlertTriangle, CircleSlash } from "lucide-react";

import { ColocaBadge } from "@/shared/components/data-display/ColocaBadge";
import { ColocaSurface } from "@/shared/components/layout/ColocaSurface";
import useTranslations from "@/shared/lang/useTranslations";
import { cn } from "@/shared/lib/utils";

import type { RateProviderUsage } from "../../types/rateProviderUsage.types";

interface ProviderPriorityLadderProps {
  providers: RateProviderUsage[];
  formatCount: (value: number) => string;
  /** Takes a 0–1 ratio, as `Intl.NumberFormat` with `style: "percent"` does. */
  formatPercent: (ratio: number) => string;
}

/**
 * Share of calls the provider actually resolved, as a whole percent.
 *
 * Clamped away from the extremes rather than rounded to them: on 999 of 1000 a plain round reads 100 %, and
 * the one failure — the only reason to open this screen — disappears behind a number that says everything is
 * fine. 100 % is therefore reserved for a provider that failed nothing and 0 % for one that resolved
 * nothing.
 */
const successPercent = ({ total, success }: RateProviderUsage): number => {
  if (total === 0 || success === 0) return 0;
  if (success === total) return 100;
  return Math.min(99, Math.max(1, Math.round((success / total) * 100)));
};

/**
 * The failover order as a ladder, each rung carrying what that provider actually cost and whether it
 * answered.
 *
 * The rank is the point of the component, not decoration: providers are tried in this exact order and the
 * first success wins inside the same request. That is why a rung can show a full bar of spend and zero
 * resolved rates — the quota is charged by the attempt, and the rung below it silently served the rate. Read
 * as separate tiles those two facts never meet; stacked in order they are one sentence.
 */
export const ProviderPriorityLadder = ({
  providers,
  formatCount,
  formatPercent,
}: ProviderPriorityLadderProps) => {
  const { t } = useTranslations("rateProviderUsage");

  return (
    <ColocaSurface padding="none" className="divide-y overflow-hidden">
      {providers.map((provider) => {
        const neverAnswered = provider.total > 0 && provider.success === 0;
        const successShare = provider.total > 0 ? (provider.success / provider.total) * 100 : 0;
        const resolvedPercent = successPercent(provider);
        const dominantError = provider.errors_by_code[0];

        return (
          <div key={provider.provider_code} className="flex flex-col gap-3 p-4 sm:flex-row sm:gap-4">
            <div className="flex shrink-0 items-start">
              {provider.priority === null ? (
                <span
                  className="flex size-8 items-center justify-center rounded-full bg-muted text-muted-foreground"
                  title={t("ladder_retired_hint")}
                >
                  <CircleSlash className="size-4" />
                </span>
              ) : (
                <span className="flex size-8 items-center justify-center rounded-full bg-primary/10 text-sm font-semibold text-primary tabular-nums dark:bg-primary/20">
                  {provider.priority}
                </span>
              )}
            </div>

            <div className="min-w-0 flex-1 space-y-2">
              <div className="flex flex-wrap items-center gap-2">
                <span className="font-mono text-sm font-medium tracking-tight">{provider.provider_code}</span>
                {provider.priority === 1 ? (
                  <ColocaBadge variant="secondary">{t("ladder_role_primary")}</ColocaBadge>
                ) : provider.priority === null ? (
                  <ColocaBadge variant="ghost">{t("ladder_role_retired")}</ColocaBadge>
                ) : (
                  <ColocaBadge variant="ghost">
                    {t("ladder_role_fallback", { position: provider.priority - 1 })}
                  </ColocaBadge>
                )}
                {neverAnswered ? (
                  <ColocaBadge variant="destructive" icon={AlertTriangle}>
                    {t("ladder_never_answered")}
                  </ColocaBadge>
                ) : null}
              </div>

              {provider.total > 0 ? (
                <>
                  <div className="flex items-center gap-2">
                    <div
                      className="flex h-2 min-w-0 flex-1 overflow-hidden rounded-full bg-muted"
                      role="img"
                      aria-label={t("ladder_bar_aria", {
                        success: formatCount(provider.success),
                        failed: formatCount(provider.failed),
                      })}
                    >
                      {/* Resolved first, so the bar fills left to right as the provider works. */}
                      <div className="bg-success" style={{ width: `${successShare}%` }} />
                      <div className="bg-destructive" style={{ width: `${100 - successShare}%` }} />
                    </div>
                    <span
                      className={cn(
                        "shrink-0 text-xs font-medium tabular-nums",
                        resolvedPercent === 100 && "text-success",
                        resolvedPercent === 0 && "text-destructive",
                      )}
                    >
                      {formatPercent(resolvedPercent / 100)}
                    </span>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    <span className="text-success">
                      {t("ladder_resolved", {
                        value: formatCount(provider.success),
                      })}
                    </span>
                    {" · "}
                    <span className={cn(provider.failed > 0 && "text-destructive")}>
                      {t("ladder_failed", { value: formatCount(provider.failed) })}
                    </span>
                    {dominantError ? (
                      <>
                        {" · "}
                        <span className="font-mono">
                          {dominantError.error_code ?? t("ladder_error_unknown")}
                        </span>
                      </>
                    ) : null}
                  </p>
                </>
              ) : (
                <p className="text-xs text-muted-foreground">{t("ladder_no_requests")}</p>
              )}
            </div>

            <div className="shrink-0 text-left sm:text-right">
              <p className="text-2xl font-semibold tabular-nums leading-none">
                {formatCount(provider.total)}
              </p>
              <p className="mt-1 text-xs text-muted-foreground">{t("ladder_requests_label")}</p>
            </div>
          </div>
        );
      })}
    </ColocaSurface>
  );
};
